<template>
  <div v-if="isTenantPlusSubscriber">
    <div v-if="listingStats" id="listingStats">
      <div :class="$style.headerWrapper">
        <h2>{{ t('listingStats.title') }}</h2>
        <HgBadge :class="$style.badge" theme="primarylight">
          {{ t('listingStats.fields.badge') }}
          <HgIcon :class="$style.tenantPlusIcon" icon="TenantPlus" size="x-small" />
        </HgBadge>
      </div>
      <div :class="[$style.wrapper]">
        <div
          v-for="listingStatItem in listingStats"
          :key="listingStatItem.title"
          data-test="contentWrapper"
          :class="$style.contentWrapper"
        >
          <div :class="$style.title">
            <HgIcon
              :class="$style.icon"
              :icon="listingStatItem.icon"
              size="large"
              :theme="isPlatformHg() ? 'secondary' : 'dark'"
            />
            <span>{{ listingStatItem.title }}</span>
          </div>
          <h2 :class="$style.valueWrapper">{{ listingStatItem.value }}</h2>
        </div>
      </div>
    </div>
  </div>
  <ListingStatsBlurry
    v-else-if="isListingStatsBlurryListing && isRentObject"
    :entryPoint="getEntryPoint"
  />
</template>

<script lang="ts" setup>
import { HgBadge, HgIcon, Icon } from '@smg-real-estate/domus';
import { Listing, OfferType } from '@smg-real-estate/hg-rets-schema';
import { computed, ref, watch } from 'vue';

import ListingStatsBlurry from '@/components/ListingStats/ListingStatsBlurry.vue';
import { getListingStats } from '@/helpers/ListingStats/ListingStats';
import { ListingStats, listingStatsListerPrefixes } from '@/helpers/ListingStats/types';
import { isPlatformHg } from '@/helpers/platformDetection';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

defineOptions({
  name: 'ListingStats',
});

interface Props {
  isTenantPlusSubscriber: boolean;
  listing: Listing;
}
const props = defineProps<Props>();

const listingStatsObject = ref<ListingStats | null>(null);

const listingStats = computed(() => {
  return listingStatsObject.value
    ? [
        {
          title: t('listingStats.fields.viewed') as string,
          value: listingStatsObject.value.viewedNumber,
          icon: Icon.Visibility,
        },
        {
          title: t('listingStats.fields.onlineSince') as string,
          value: t(
            'listingStats.fields.onlineSinceNDays',
            listingStatsObject.value.onlineDays,
          ) as string,
          icon: Icon.Calendar,
        },
        {
          title: t('listingStats.fields.contactedByPhone') as string,
          value: listingStatsObject.value.contactedPhoneNumber,
          icon: Icon.Phone,
        },
        {
          title: t('listingStats.fields.contactedByEmail') as string,
          value: listingStatsObject.value.contactedEmailNumber,
          icon: Icon.Email,
        },
      ]
    : undefined;
});
const isRentObject = computed(() => props.listing?.offerType === OfferType.Rent);
const isListingStatsBlurryListing = computed(() => {
  return listingStatsListerPrefixes.some((prefix) => props.listing?.lister?.id?.startsWith(prefix));
});
const getEntryPoint = computed(() => {
  return `pdp_${props.listing?.offerType?.toLowerCase()}_listing_stats`;
});

const fetchListingStats = async (): Promise<void> => {
  listingStatsObject.value = await getListingStats(props.listing.id as string);
};

watch(
  () => props.isTenantPlusSubscriber,
  async (newValue) => {
    if (newValue) {
      await fetchListingStats();
    }
  },
  { immediate: true },
);
</script>

<style lang="scss" module>
@import '@/assets/variables';

.wrapper {
  position: relative;
  display: flex;
  flex-flow: column wrap;
  padding-bottom: $space-50;
  text-align: center;
  border-top: 1px solid $color-grey-50;
}

.contentWrapper {
  padding-top: $space-100;
  border-bottom: 1px solid $color-grey-50;
}

.title {
  display: flex;
  flex-direction: row;
  justify-content: center;
  color: $color-grey-90;
}

.headerWrapper {
  display: flex;

  .badge {
    align-self: center;
    height: 50%;
    margin-left: $space-100;
  }
}

.valueWrapper {
  margin: $space-50 0 $space-100;
}

.icon {
  margin-right: $space-50;
}

.tenantPlusIcon {
  margin: auto;
}

.showMore {
  padding-top: $space-75;
  font-weight: 700;
}

@media (min-width: $viewport-sm-min) {
  .wrapper {
    display: inline-flex;
    flex-direction: row;

    // To draw divider
    &::after {
      position: absolute;
      top: $space-50;
      bottom: $space-50;
      left: 50%;
      z-index: 1;
      width: 1px;
      content: '';
      background-color: $color-grey-50;
    }
  }

  .contentWrapper {
    flex-basis: 50%;
  }
}
</style>
